import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Divider,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Select,
  Spinner,
  Stack,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { WarningTwoIcon } from "@chakra-ui/icons";
import { useCallback, useEffect, useState } from "react";
import actions from "src/actions";
import { Button } from "../components/Button";
import type {
  MetaImportApplyResponse,
  MetaImportPreviewPayload,
  MetaImportPersistPreview,
  MetaImportPersistPreviewMatchedAdSets,
} from "./types";

function messageFromUnknown(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback;
}

const CAMPAIGN_FIELD_LABELS: Record<string, string> = {
  name: "Campaign name",
  status: "Status",
  account_id: "Ad account id",
  external_id: "Meta campaign id",
  updated_time: "Meta updated time",
  user_updated_at: "Last user update",
  budget: "Budget",
};

function formatPreviewValue(value: unknown): string {
  if (value === undefined || value === null) return "—";
  if (typeof value === "string") return value || "—";
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function ImportDryRunMatchedAdSetsSection({
  matched,
}: {
  matched: MetaImportPersistPreviewMatchedAdSets;
}) {
  const { rows, totals } = matched;
  return (
    <Box>
      <Text fontSize="sm" fontWeight="semibold" mb={2}>
        Matched ad sets — ads and creatives
      </Text>
      {rows.length === 0 ? (
        <Text fontSize="sm" color="gray.600">
          No existing KYD ad sets were reused; every imported row uses a new KYD
          ad set id. Ads and creatives come entirely from Meta.
        </Text>
      ) : (
        <>
          <HStack flexWrap="wrap" gap={2} mb={3} fontSize="sm">
            <Badge colorScheme="purple">Matched ad sets: {rows.length}</Badge>
            <Badge
              colorScheme={totals.creative_updates > 0 ? "orange" : "gray"}
            >
              Creative updates: {totals.creative_updates}
            </Badge>
            <Badge colorScheme={totals.meta_ads_added > 0 ? "blue" : "gray"}>
              New Meta ads: {totals.meta_ads_added}
            </Badge>
            <Badge colorScheme={totals.meta_ads_removed > 0 ? "red" : "gray"}>
              Meta ads removed vs KYD: {totals.meta_ads_removed}
            </Badge>
          </HStack>
          <VStack align="stretch" spacing={2} maxH="36vh" overflowY="auto">
            {rows.map((row) => (
              <Box
                key={row.kyd_adset_id}
                p={3}
                borderWidth="1px"
                borderRadius="md"
                borderColor="gray.200"
              >
                <Text fontWeight="medium" fontSize="sm">
                  {row.name}
                </Text>
                <Text fontSize="xs" color="gray.500" fontFamily="mono">
                  KYD {row.kyd_adset_id}
                </Text>
                <Text fontSize="sm" color="gray.700" mt={2}>
                  {row.changes_summary}
                </Text>
              </Box>
            ))}
          </VStack>
        </>
      )}
    </Box>
  );
}

export type ImportFromMetaControlProps = {
  campaignId: string | undefined;
  hasMetaExternalId: boolean;
  /** Same gate as Ads in the sidebar (`platforms/write`). */
  canUse: boolean;
  isApplyingCampaignChanges?: boolean;
  /** Lets the parent include Meta apply in global “busy” (e.g. disable other header actions). */
  onImportApplyLoadingChange?: (loading: boolean) => void;
  onAfterImport: () => void | Promise<void>;
};

export default function ImportFromMetaControl({
  campaignId,
  hasMetaExternalId,
  canUse,
  isApplyingCampaignChanges = false,
  onImportApplyLoadingChange,
  onAfterImport,
}: ImportFromMetaControlProps) {
  const toast = useToast();
  const {
    isOpen: importFromMetaOpen,
    onOpen: onOpenImportFromMeta,
    onClose: onCloseImportFromMeta,
  } = useDisclosure();
  const [metaImportPreview, setMetaImportPreview] =
    useState<MetaImportPreviewPayload | null>(null);
  const [metaImportMap, setMetaImportMap] = useState<Record<string, string>>(
    {}
  );
  const [metaImportMode, setMetaImportMode] = useState<
    "match" | "replace_from_meta"
  >("match");
  const [metaImportPreviewLoading, setMetaImportPreviewLoading] =
    useState(false);
  const [metaImportPreviewError, setMetaImportPreviewError] = useState<
    string | null
  >(null);
  const [isImportingFromMeta, setIsImportingFromMeta] = useState(false);
  const [importDryRun, setImportDryRun] = useState<{
    preview: MetaImportPersistPreview;
    imported_adset_count: number;
  } | null>(null);

  const closeImportFromMeta = useCallback(() => {
    onCloseImportFromMeta();
    setMetaImportPreview(null);
    setMetaImportMap({});
    setMetaImportPreviewError(null);
    setMetaImportMode("match");
    setImportDryRun(null);
  }, [onCloseImportFromMeta]);

  useEffect(() => {
    if (!importFromMetaOpen || !campaignId) return;
    let cancelled = false;
    (async () => {
      setMetaImportPreviewLoading(true);
      setMetaImportPreviewError(null);
      try {
        const data = (await actions.ADMIN.previewImportCampaignFromMeta({
          campaign_id: campaignId,
          import_mode: metaImportMode,
        })) as MetaImportPreviewPayload;
        if (cancelled) return;
        setMetaImportPreview(data);
        const initial: Record<string, string> = {};
        for (const r of data.proposed_links) {
          initial[r.meta_adset_id] = r.proposed_kyd_adset_id ?? "";
        }
        setMetaImportMap(initial);
      } catch (e: unknown) {
        if (!cancelled) {
          setMetaImportPreview(null);
          setMetaImportPreviewError(
            messageFromUnknown(e, "Could not load Meta import preview")
          );
        }
      } finally {
        if (!cancelled) setMetaImportPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [importFromMetaOpen, campaignId, metaImportMode]);

  const buildApplyPayload = useCallback(() => {
    if (!campaignId || !metaImportPreview) return null;
    const meta_adset_id_to_kyd_adset_id: Record<string, string> = {};
    let fill_missing_meta_adsets_with_new_kyd_ids = false;
    for (const row of metaImportPreview.proposed_links) {
      const v = (metaImportMap[row.meta_adset_id] ?? "").trim();
      if (!v) {
        fill_missing_meta_adsets_with_new_kyd_ids = true;
      } else {
        meta_adset_id_to_kyd_adset_id[row.meta_adset_id] = v;
      }
    }
    return {
      campaign_id: campaignId,
      meta_adset_id_to_kyd_adset_id,
      fill_missing_meta_adsets_with_new_kyd_ids,
      meta_updated_time_if_unchanged:
        metaImportPreview.meta_stale_guard.meta_updated_time,
      tombstone_superseded_local_adsets: false,
    };
  }, [campaignId, metaImportPreview, metaImportMap]);

  const handleReviewImportFromMeta = useCallback(async () => {
    const payload = buildApplyPayload();
    if (!payload) return;
    setIsImportingFromMeta(true);
    onImportApplyLoadingChange?.(true);
    try {
      const res = (await actions.ADMIN.applyImportCampaignFromMeta({
        ...payload,
        persist: false,
      })) as MetaImportApplyResponse;
      if (res.persist_preview) {
        setImportDryRun({
          preview: res.persist_preview,
          imported_adset_count: res.imported_adset_count ?? 0,
        });
      } else {
        toast({
          title: "Could not build change preview",
          description:
            res?.message ??
            "The server did not return persist_preview. You can still try confirming the import.",
          status: "warning",
          isClosable: true,
        });
      }
    } catch (err: unknown) {
      toast({
        title: "Preview failed",
        description: messageFromUnknown(
          err,
          "Could not simulate import from Meta."
        ),
        status: "error",
        isClosable: true,
      });
    } finally {
      setIsImportingFromMeta(false);
      onImportApplyLoadingChange?.(false);
    }
  }, [buildApplyPayload, onImportApplyLoadingChange, toast]);

  const handleConfirmImportFromMeta = useCallback(async () => {
    const payload = buildApplyPayload();
    if (!payload) return;
    setIsImportingFromMeta(true);
    onImportApplyLoadingChange?.(true);
    try {
      const res = (await actions.ADMIN.applyImportCampaignFromMeta({
        ...payload,
        persist: true,
      })) as MetaImportApplyResponse;
      closeImportFromMeta();
      toast({
        title: "Imported from Meta",
        description:
          res?.imported_adset_count != null
            ? `${res.imported_adset_count} ad set(s) synced.`
            : (res?.message ?? "Campaign data was updated from Meta."),
        status: "success",
        isClosable: true,
      });
      await onAfterImport();
    } catch (err: unknown) {
      toast({
        title: "Import failed",
        description: messageFromUnknown(err, "Could not import from Meta."),
        status: "error",
        isClosable: true,
      });
    } finally {
      setIsImportingFromMeta(false);
      onImportApplyLoadingChange?.(false);
    }
  }, [
    buildApplyPayload,
    closeImportFromMeta,
    onImportApplyLoadingChange,
    onAfterImport,
    toast,
  ]);

  if (!canUse) {
    return null;
  }

  return (
    <>
      {hasMetaExternalId ? (
        <Button
          leftIcon={<WarningTwoIcon aria-hidden />}
          variant="outline"
          colorScheme="red"
          borderColor="red.400"
          color="red.700"
          _hover={{
            bg: "red.50",
            borderColor: "red.500",
            color: "red.800",
          }}
          _active={{ bg: "red.100" }}
          onClick={onOpenImportFromMeta}
          isLoading={isImportingFromMeta}
          isDisabled={isApplyingCampaignChanges}
        >
          Import from Meta
        </Button>
      ) : null}

      <Modal
        isOpen={importFromMetaOpen}
        onClose={closeImportFromMeta}
        size="xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader>Import from Meta</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              {importDryRun ? (
                <>
                  <Text fontSize="sm" color="gray.600">
                    Dry run only — nothing was saved. If you confirm, KYD will
                    persist this merged campaign and{" "}
                    {importDryRun.imported_adset_count} imported ad set(s).
                  </Text>
                  <Alert
                    status="info"
                    borderRadius="md"
                    fontSize="sm"
                    alignItems="flex-start"
                  >
                    <AlertIcon mt={0.5} />
                    <Box>
                      <AlertTitle fontSize="sm" mb={1}>
                        What happens to ad sets you mapped to Meta
                      </AlertTitle>
                      <AlertDescription fontSize="sm">
                        Matched ad sets get Meta&apos;s current targeting,
                        budget, and schedule. For ads, KYD keeps what you
                        already have when the Meta ad id matches; only ads that
                        exist on Meta but are missing in KYD are added (with
                        creatives pulled in this run). The summary below
                        compares that merge to your saved campaign.
                      </AlertDescription>
                    </Box>
                  </Alert>
                  <ImportDryRunMatchedAdSetsSection
                    matched={importDryRun.preview.matched_adsets}
                  />
                  <Divider />
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold" mb={2}>
                      Campaign field changes
                    </Text>
                    {importDryRun.preview.campaign_field_changes.length ===
                    0 ? (
                      <Text fontSize="sm" color="gray.600">
                        No campaign-level field changes detected.
                      </Text>
                    ) : (
                      <VStack align="stretch" spacing={2} fontSize="sm">
                        {importDryRun.preview.campaign_field_changes.map(
                          (c) => (
                            <Box
                              key={c.key}
                              p={2}
                              borderWidth="1px"
                              borderRadius="md"
                              borderColor="gray.200"
                            >
                              <Text fontWeight="medium">
                                {CAMPAIGN_FIELD_LABELS[c.key] ?? c.key}
                              </Text>
                              <Text
                                fontSize="xs"
                                color="gray.600"
                                fontFamily="mono"
                                whiteSpace="pre-wrap"
                                wordBreak="break-word"
                              >
                                {formatPreviewValue(c.before)}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                →
                              </Text>
                              <Text
                                fontSize="xs"
                                color="gray.800"
                                fontFamily="mono"
                                whiteSpace="pre-wrap"
                                wordBreak="break-word"
                              >
                                {formatPreviewValue(c.after)}
                              </Text>
                            </Box>
                          )
                        )}
                      </VStack>
                    )}
                  </Box>
                  <Divider />
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold" mb={2}>
                      Ad sets
                    </Text>
                    <HStack flexWrap="wrap" gap={2} mb={2} fontSize="sm">
                      <Badge colorScheme="green">
                        New KYD ad set ids:{" "}
                        {importDryRun.preview.adsets.new_kyd_adset_ids.length}
                      </Badge>
                      <Badge
                        colorScheme={
                          importDryRun.preview.adsets
                            .dropped_local_kyd_adset_ids.length > 0
                            ? "orange"
                            : "gray"
                        }
                      >
                        Dropped from campaign:{" "}
                        {
                          importDryRun.preview.adsets
                            .dropped_local_kyd_adset_ids.length
                        }
                      </Badge>
                    </HStack>
                    {importDryRun.preview.adsets.new_kyd_adset_ids.length >
                      0 && (
                      <Text
                        fontSize="xs"
                        fontFamily="mono"
                        color="gray.700"
                        mb={2}
                        whiteSpace="pre-wrap"
                        wordBreak="break-all"
                      >
                        {importDryRun.preview.adsets.new_kyd_adset_ids.join(
                          ", "
                        )}
                      </Text>
                    )}
                    {importDryRun.preview.adsets.dropped_local_kyd_adset_ids
                      .length > 0 && (
                      <Text
                        fontSize="xs"
                        fontFamily="mono"
                        color="orange.700"
                        whiteSpace="pre-wrap"
                        wordBreak="break-all"
                      >
                        {importDryRun.preview.adsets.dropped_local_kyd_adset_ids.join(
                          ", "
                        )}
                      </Text>
                    )}
                  </Box>
                </>
              ) : (
                <>
                  <Text fontSize="sm" color="gray.600">
                    Review how Meta ad sets map to KYD ad sets, adjust pairings
                    if needed, then review changes. Unmapped rows get new KYD ad
                    set ids on import.
                  </Text>
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold" mb={2}>
                      Import strategy
                    </Text>
                    <RadioGroup
                      value={metaImportMode}
                      onChange={(v) =>
                        setMetaImportMode(v as "match" | "replace_from_meta")
                      }
                    >
                      <Stack direction="column" spacing={2}>
                        <Radio value="match">
                          Match — reuse KYD ids when external_id or unique name
                          lines up (recommended)
                        </Radio>
                        <Radio value="replace_from_meta">
                          Replace from Meta — default every Meta ad set to a new
                          KYD id unless you map it below
                        </Radio>
                      </Stack>
                    </RadioGroup>
                  </Box>
                  {metaImportPreviewLoading ? (
                    <HStack justify="center" py={8}>
                      <Spinner />
                      <Text>Loading Meta preview…</Text>
                    </HStack>
                  ) : metaImportPreviewError ? (
                    <Text color="red.600" fontSize="sm">
                      {metaImportPreviewError}
                    </Text>
                  ) : metaImportPreview ? (
                    <>
                      <HStack flexWrap="wrap" gap={2} fontSize="sm">
                        <Badge>
                          Meta ad sets:{" "}
                          {metaImportPreview.match_summary.total_meta_adsets}
                        </Badge>
                        <Badge colorScheme="green">
                          Matched (Meta id):{" "}
                          {
                            metaImportPreview.match_summary
                              .matched_by_external_id
                          }
                        </Badge>
                        <Badge colorScheme="blue">
                          Matched (name):{" "}
                          {
                            metaImportPreview.match_summary
                              .matched_by_unique_name
                          }
                        </Badge>
                        <Badge colorScheme="orange">
                          Unmatched:{" "}
                          {
                            metaImportPreview.match_summary
                              .unmatched_meta_adsets
                          }
                        </Badge>
                      </HStack>
                      <VStack
                        align="stretch"
                        spacing={3}
                        maxH="50vh"
                        overflowY="auto"
                      >
                        {metaImportPreview.proposed_links.map((row) => (
                          <Box
                            key={row.meta_adset_id}
                            p={3}
                            borderWidth="1px"
                            borderRadius="md"
                            borderColor="gray.200"
                          >
                            <HStack
                              justify="space-between"
                              align="flex-start"
                              flexWrap="wrap"
                              gap={2}
                            >
                              <Box flex="1" minW="200px">
                                <Text fontWeight="medium" fontSize="sm">
                                  {row.meta_name || "Untitled"}
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                  Meta {row.meta_adset_id}
                                </Text>
                                <Badge mt={1} fontSize="xs">
                                  {row.match_reason === "external_id"
                                    ? "Meta id match"
                                    : row.match_reason === "unique_name"
                                      ? "Name match"
                                      : "Unmatched"}
                                </Badge>
                              </Box>
                              <Select
                                size="sm"
                                maxW="280px"
                                value={metaImportMap[row.meta_adset_id] ?? ""}
                                onChange={(e) =>
                                  setMetaImportMap((m) => ({
                                    ...m,
                                    [row.meta_adset_id]: e.target.value,
                                  }))
                                }
                              >
                                <option value="">New KYD ad set</option>
                                {metaImportPreview.kyd_adsets.map((k) => (
                                  <option key={k.id} value={k.id}>
                                    {k.name || "Untitled"} ({k.id})
                                  </option>
                                ))}
                              </Select>
                            </HStack>
                          </Box>
                        ))}
                      </VStack>
                    </>
                  ) : null}
                </>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            {importDryRun ? (
              <>
                <Button
                  variant="ghost"
                  mr="auto"
                  onClick={() => setImportDryRun(null)}
                >
                  Back to mapping
                </Button>
                <Button variant="ghost" mr={3} onClick={closeImportFromMeta}>
                  Cancel
                </Button>
                <Button
                  bg="black"
                  color="white"
                  borderColor="black"
                  _hover={{ bg: "gray.800" }}
                  isLoading={isImportingFromMeta}
                  onClick={() => void handleConfirmImportFromMeta()}
                >
                  Confirm import
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" mr={3} onClick={closeImportFromMeta}>
                  Cancel
                </Button>
                <Button
                  bg="black"
                  color="white"
                  borderColor="black"
                  _hover={{ bg: "gray.800" }}
                  isLoading={isImportingFromMeta}
                  isDisabled={
                    !metaImportPreview ||
                    metaImportPreviewLoading ||
                    !!metaImportPreviewError ||
                    metaImportPreview.proposed_links.length === 0
                  }
                  onClick={() => void handleReviewImportFromMeta()}
                >
                  Review changes
                </Button>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
